// OMNI Selection Component Recipes for Chakra UI v3
// Provides consistent styling for selection UI components (badges, tabs, selectable items)

import { defineRecipe } from '@chakra-ui/react';

// Universal Selectable Item Recipe (for table items, list selections, etc.)
export const omniSelectableItemRecipe = defineRecipe({
    className: 'omni-selectable-item',

    base: {
        display: 'flex',
        alignItems: 'center',
        padding: '12px 16px',
        borderRadius: 'base',
        cursor: 'pointer',
        transition: 'all 200ms ease',
        position: 'relative',
        overflow: 'hidden',
        userSelect: 'none',

        // Default OMNI styling - white background with dark text and subtle shadow
        bg: 'white',
        color: 'navbar.500', // Dark blue-gray for excellent contrast
        boxShadow:
            '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',

        _hover: {
            bg: 'brand.50',
            boxShadow:
                '0 4px 6px -1px rgba(0, 161, 210, 0.1), 0 2px 4px -1px rgba(0, 161, 210, 0.06)',
        },

        _focus: {
            outline: '2px solid var(--chakra-colors-brand-400)',
            outlineOffset: '2px',
        },
    },

    variants: {
        // Different selection states
        state: {
            default: {
                bg: 'white',
                color: 'navbar.500',
            },

            selected: {
                bg: 'brand.500',
                color: 'white',
                boxShadow:
                    '0 4px 6px -1px rgba(0, 161, 210, 0.25), 0 2px 4px -1px rgba(0, 161, 210, 0.15)',

                _hover: {
                    bg: 'brand.600',
                    boxShadow:
                        '0 6px 8px -2px rgba(0, 161, 210, 0.3), 0 3px 6px -2px rgba(0, 161, 210, 0.2)',
                },
            },

            disabled: {
                bg: 'gray.100',
                color: 'gray.400',
                cursor: 'not-allowed',
                boxShadow: 'none',

                _hover: {
                    bg: 'gray.100',
                    boxShadow: 'none',
                },
            },
        },

        // Different sizes
        size: {
            sm: {
                padding: '8px 12px',
                fontSize: 'sm',
            },
            md: {
                padding: '12px 16px',
                fontSize: 'md',
            },
            lg: {
                padding: '16px 20px',
                fontSize: 'lg',
            },
        },
    },

    defaultVariants: {
        state: 'default',
        size: 'md',
    },
});

// Universal Selection Badge Recipe (for client badges, tag selections, etc.)
export const omniSelectionBadgeRecipe = defineRecipe({
    className: 'omni-selection-badge',

    base: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '6px 12px',
        borderRadius: 'full',
        fontSize: 'sm',
        fontWeight: 'medium',
        cursor: 'pointer',
        transition: 'all 200ms ease',
        userSelect: 'none',
        lineHeight: '1.2',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',

        // Default OMNI styling - white background with dark text and subtle shadow
        bg: 'white',
        color: 'navbar.500',
        boxShadow:
            '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',

        _hover: {
            bg: 'gray.50',
            boxShadow:
                '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            transform: 'translateY(-1px)',
        },

        _active: {
            transform: 'translateY(0)',
            boxShadow:
                '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
        },

        _focus: {
            outline: '2px solid var(--chakra-colors-brand-400)',
            outlineOffset: '2px',
        },
    },

    variants: {
        // Different selection states
        state: {
            default: {
                bg: 'white',
                color: 'navbar.500',

                _hover: {
                    bg: 'gray.50',
                },
            },

            selected: {
                bg: 'brand.500',
                color: 'white',
                boxShadow:
                    '0 4px 6px -1px rgba(0, 161, 210, 0.25), 0 2px 4px -1px rgba(0, 161, 210, 0.15)',

                _hover: {
                    bg: 'brand.600',
                    boxShadow:
                        '0 6px 8px -2px rgba(0, 161, 210, 0.3), 0 3px 6px -2px rgba(0, 161, 210, 0.2)',
                    transform: 'translateY(-1px)',
                },
            },

            removable: {
                bg: 'red.50',
                color: 'red.600',

                _hover: {
                    bg: 'red.100',
                    color: 'red.700',
                },
            },

            disabled: {
                bg: 'gray.100',
                color: 'gray.400',
                cursor: 'not-allowed',

                _hover: {
                    bg: 'gray.100',
                    transform: 'none',
                    boxShadow:
                        '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
                },
            },
        },

        // Different sizes
        size: {
            xs: {
                padding: '4px 8px',
                fontSize: 'xs',
            },
            sm: {
                padding: '6px 12px',
                fontSize: 'sm',
            },
            md: {
                padding: '8px 16px',
                fontSize: 'md',
            },
            lg: {
                padding: '10px 20px',
                fontSize: 'lg',
            },
        },
    },

    defaultVariants: {
        state: 'default',
        size: 'sm',
    },
});

// Universal Tab Button Recipe (for date selector tabs, navigation tabs, etc.)
export const omniTabButtonRecipe = defineRecipe({
    className: 'omni-tab-button',

    base: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '12px 16px',
        borderRadius: 'base',
        fontSize: 'md',
        fontWeight: 'medium',
        cursor: 'pointer',
        transition: 'all 200ms ease',
        userSelect: 'none',
        position: 'relative',
        overflow: 'hidden',

        // Default OMNI styling - white background with dark text and subtle shadow
        bg: 'white',
        color: 'navbar.500',
        boxShadow:
            '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',

        _hover: {
            bg: 'brand.50',
            boxShadow:
                '0 4px 6px -1px rgba(0, 161, 210, 0.1), 0 2px 4px -1px rgba(0, 161, 210, 0.06)',
        },

        _focus: {
            outline: '2px solid var(--chakra-colors-brand-400)',
            outlineOffset: '2px',
        },

        _active: {
            transform: 'scale(0.98)',
        },
    },

    variants: {
        // Different tab states
        state: {
            default: {
                bg: 'white',
                color: 'navbar.500',

                _hover: {
                    bg: 'brand.50',
                },
            },

            selected: {
                bg: 'brand.500',
                color: 'white',
                boxShadow:
                    '0 4px 6px -1px rgba(0, 161, 210, 0.25), 0 2px 4px -1px rgba(0, 161, 210, 0.15)',

                _hover: {
                    bg: 'brand.600',
                    boxShadow:
                        '0 6px 8px -2px rgba(0, 161, 210, 0.3), 0 3px 6px -2px rgba(0, 161, 210, 0.2)',
                },
            },

            disabled: {
                bg: 'gray.100',
                color: 'gray.400',
                cursor: 'not-allowed',
                boxShadow: 'none',

                _hover: {
                    bg: 'gray.100',
                    boxShadow: 'none',
                },

                _active: {
                    transform: 'none',
                },
            },
        },

        // Different sizes
        size: {
            sm: {
                padding: '8px 12px',
                fontSize: 'sm',
                minHeight: '32px',
            },
            md: {
                padding: '12px 16px',
                fontSize: 'md',
                minHeight: '40px',
            },
            lg: {
                padding: '16px 20px',
                fontSize: 'lg',
                minHeight: '48px',
            },
        },

        // Different orientations for tab groups
        orientation: {
            horizontal: {
                minWidth: 'auto',
            },
            vertical: {
                width: '100%',
                justifyContent: 'flex-start',
            },
        },
    },

    defaultVariants: {
        state: 'default',
        size: 'md',
        orientation: 'horizontal',
    },
});

// OMNI Input Recipe Extension (for consistent form inputs)
export const omniInputRecipe = defineRecipe({
    className: 'omni-input',

    base: {
        width: '100%',
        padding: '12px 16px',
        borderRadius: 'base',
        fontSize: 'md',
        lineHeight: '1.5',
        transition: 'all 200ms ease',

        // OMNI styling - white background with dark text, borderless with shadow
        bg: 'white',
        color: 'navbar.500',
        border: 'none',
        boxShadow:
            '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',

        _placeholder: {
            color: 'gray.400',
        },

        _hover: {
            boxShadow:
                '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        },

        _focus: {
            outline: '2px solid var(--chakra-colors-brand-400)',
            outlineOffset: '2px',
            boxShadow:
                '0 4px 6px -1px rgba(0, 161, 210, 0.1), 0 2px 4px -1px rgba(0, 161, 210, 0.06)',
        },

        _invalid: {
            boxShadow:
                '0 1px 3px 0 rgba(239, 68, 68, 0.1), 0 1px 2px 0 rgba(239, 68, 68, 0.06)',

            _focus: {
                outline: '2px solid var(--chakra-colors-red-400)',
                outlineOffset: '2px',
                boxShadow:
                    '0 4px 6px -1px rgba(239, 68, 68, 0.1), 0 2px 4px -1px rgba(239, 68, 68, 0.06)',
            },
        },

        _disabled: {
            bg: 'gray.100',
            color: 'gray.400',
            cursor: 'not-allowed',

            _placeholder: {
                color: 'gray.300',
            },
        },
    },

    variants: {
        size: {
            sm: {
                padding: '8px 12px',
                fontSize: 'sm',
            },
            md: {
                padding: '12px 16px',
                fontSize: 'md',
            },
            lg: {
                padding: '16px 20px',
                fontSize: 'lg',
            },
        },
    },

    defaultVariants: {
        size: 'md',
    },
});
